import { switchAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, defineStyle } from '@chakra-ui/react';
import { calc, cssVar } from '@chakra-ui/theme-tools';
import { dls } from '@joinrepublic/dls-ui-core';

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(parts.keys);

const $width = cssVar('switch-track-width');
const $height = cssVar('switch-track-height');
const $diff = cssVar('switch-track-diff');
const $translateX = cssVar('switch-thumb-x');

// Chakra measures the track content-box with 2px of padding, so the vars below are the Figma
// size less 4. The thumb is a square of the track height and travels `width - height`.
const container = defineStyle({
  [$diff.variable]: calc.subtract($width, $height),
  [$translateX.variable]: $diff.reference,
  _rtl: {
    [$translateX.variable]: calc($diff).negate().toString(),
  },
});

const track = defineStyle({
  width: $width.reference,
  height: $height.reference,
  borderRadius: 'full',
  p: '0.5',
  bg: 'neutral.fill-subtle',
  transitionProperty: 'background-color',
  transitionDuration: 'normal',
  _hover: { bg: 'neutral.fill-active' },
  _checked: {
    bg: 'brand.solid-primary',
    _hover: { bg: 'brand.solid-hover' },
  },
  // The set names disabled colours, so use them instead of Chakra's 0.4 dim.
  _disabled: {
    opacity: 1,
    cursor: 'not-allowed',
    bg: 'neutral.fill-subtle',
    _hover: { bg: 'neutral.fill-subtle' },
    _checked: {
      bg: 'neutral.solid-muted',
      _hover: { bg: 'neutral.solid-muted' },
    },
  },
  // The set draws no focus state; ring taken from the prototype.
  _focusVisible: {
    boxShadow: 'none',
    outline: '2px solid',
    outlineColor: 'brand.border-base',
    outlineOffset: '2px',
  },
});

const thumb = defineStyle({
  width: $height.reference,
  height: $height.reference,
  borderRadius: 'inherit',
  bg: 'misc.white-fix-primary',
  // Figma's `shadow/md`; `shadows.ts` has no `md`.
  boxShadow: '0px 4px 6px 1px #0000001A',
  transitionProperty: 'transform',
  transitionDuration: 'normal',
  _checked: {
    transform: `translateX(${$translateX.reference})`,
  },
  _disabled: { bg: 'misc.white-fix-secondary' },
});

const label = defineStyle({
  position: 'absolute',
  top: 0,
  bottom: 0,
  insetInlineEnd: '2px',
  // The travel; `--switch-thumb-x` is negated under `_rtl`.
  width: $diff.reference,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Chakra spaces the label off the track; here it sits inside.
  marginStart: 0,
  pointerEvents: 'none',
  fontWeight: 'semibold',
  color: 'neutral.fg-primary',
  transitionProperty: 'color',
  transitionDuration: 'normal',
  'input:checked ~ &': {
    insetInlineEnd: 'auto',
    insetInlineStart: '2px',
    color: 'misc.white-fix-primary',
  },
  // Disabled: white on a filled track, tertiary on an empty one.
  'input:disabled ~ &': { color: 'neutral.fg-tertiary' },
  'input:disabled:checked ~ &': { color: 'misc.white-fix-secondary' },
});

// Figma's `-bold` steps are this step at the `semibold` weight `baseStyle` sets.
const size = (
  width: string,
  height: string,
  step: keyof typeof dls.textStyles
) =>
  definePartsStyle({
    container: {
      [$width.variable]: width,
      [$height.variable]: height,
    },
    label: dls.textStyles[step],
  });

export const styleConfig = defineMultiStyleConfig({
  baseStyle: definePartsStyle({ container, track, thumb, label }),
  sizes: {
    sm: size('3.25rem', '1.5rem', 'body-300'), // 52x24, so 56x28 outer
    md: size('4.25rem', '2rem', 'body-500'), // 68x32, so 72x36 outer
    lg: size('5.25rem', '2.75rem', 'body-500'), // 84x44, so 88x48 outer
  },
  defaultProps: { size: 'md' },
});
